import { X } from 'lucide-react';
import { useEffect, useMemo } from 'react';

import { Badge } from '@/shared/components/shadcn/badge';
import { formatNumber } from '@/shared/utils/format-number';

import { CampaignCreatorBulkPasteDialog } from '../../overlays/campaign-creator-bulk-paste-dialog';
import { CampaignCreatorCsvImportDialog } from '../../overlays/campaign-creator-csv-import-dialog';
import {
  CampaignKeywordInputCanvas,
  FieldInfoIcon,
  FormSectionHeader,
  StepSubsectionTitle,
} from '../../primitives';
import type { CampaignCreatorDraft } from '../../types';
import { CampaignCreatorInputCanvas } from './campaign-creator-input-canvas';
import { CampaignCreatorSavedListPicker } from './campaign-creator-saved-list-picker';
import { useCampaignCreatorBulkPaste } from './hooks/use-campaign-creator-bulk-paste';
import { useCampaignCreatorCsvImport } from './hooks/use-campaign-creator-csv-import';
import { useCampaignCreatorKeywords } from './hooks/use-campaign-creator-keywords';
import { useCampaignCreatorRows } from './hooks/use-campaign-creator-rows';
import { useCampaignCreatorSavedListImport } from './hooks/use-campaign-creator-saved-list-import';
import { useCampaignCreatorValidation } from './hooks/use-campaign-creator-validation';

type Props = {
  campaignItemSlotLimit: number;
  draft: CampaignCreatorDraft;
  remainingSlots: number;
  validationRequest: number;
};

export function CampaignCreatorsSection({
  campaignItemSlotLimit,
  draft,
  remainingSlots,
  validationRequest,
}: Props) {
  const draftCreatorItems = useMemo(
    () => draft.creatorItems.filter((item) => item.sourceValue.trim().length > 0),
    [draft.creatorItems],
  );
  const validation = useCampaignCreatorValidation({
    creatorItems: draft.creatorItems,
    draftCreatorItems,
  });
  const creatorInputRows = useCampaignCreatorRows({
    creatorItems: draft.creatorItems,
    remainingSlots,
    setCreatorItems: draft.setCreatorItems,
    validation,
  });
  const keywords = useCampaignCreatorKeywords({
    creatorKeywords: draft.creatorKeywords,
    setCreatorKeywords: draft.setCreatorKeywords,
  });
  const csvImport = useCampaignCreatorCsvImport({
    campaignItemSlotLimit,
    draftCreatorItems,
    remainingSlots,
    resetCreatorValidation: validation.resetCreatorValidation,
    setCreatorItems: draft.setCreatorItems,
  });
  const bulkPaste = useCampaignCreatorBulkPaste({
    campaignItemSlotLimit,
    draftCreatorItems,
    remainingSlots,
    resetCreatorValidation: validation.resetCreatorValidation,
    setCreatorItems: draft.setCreatorItems,
  });
  const savedListImport = useCampaignCreatorSavedListImport({
    creatorItems: draft.creatorItems,
    draftCreatorItems,
    remainingSlots,
    resetCreatorValidation: validation.resetCreatorValidation,
    setCreatorItems: draft.setCreatorItems,
  });

  const creatorInputValidation = useMemo(
    () => ({
      invalidRowIds: validation.creatorInvalidRowIds,
      issues: validation.creatorIssues,
    }),
    [validation.creatorInvalidRowIds, validation.creatorIssues],
  );
  const { validateCreatorRows } = validation;

  useEffect(() => {
    if (validationRequest === 0) return;
    validateCreatorRows();
  }, [validateCreatorRows, validationRequest]);

  return (
    <section className="space-y-6">
      <div className="space-y-8">
        <div className="space-y-3">
          <StepSubsectionTitle title="Add creators" />

          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <CampaignCreatorSavedListPicker
              onImportCreatorsFromSavedList={savedListImport.importCreatorsFromSavedList}
              remainingSlots={remainingSlots}
              savedLists={savedListImport.savedLists}
              savedListsUnavailable={savedListImport.savedListsUnavailable}
              selectedCreatorLists={savedListImport.selectedCreatorLists}
            />
            <CampaignCreatorBulkPasteDialog
              campaignItemSlotLimit={campaignItemSlotLimit}
              onApplyBulkPaste={bulkPaste.applyCreatorBulkPaste}
              onGetBulkPastePreview={bulkPaste.getCreatorBulkPastePreview}
              remainingSlots={remainingSlots}
            />
            <CampaignCreatorCsvImportDialog
              onApplyCsvImport={csvImport.applyCreatorCsvImport}
              onDownloadTemplate={csvImport.downloadCreatorTemplate}
              onPreviewCsvImport={csvImport.previewCreatorCsvImport}
              remainingSlots={remainingSlots}
            />
          </div>

          {savedListImport.selectedCreatorLists.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {savedListImport.selectedCreatorLists.map((list) => (
                <Badge
                  key={list.id}
                  variant="secondary"
                  className="flex items-center gap-1.5 px-2 py-1 text-[12px]"
                >
                  <span className="truncate">
                    {list.title} ({formatNumber(list.importedCount)})
                  </span>
                  <button
                    type="button"
                    onClick={() => savedListImport.clearSelectedCreatorList(list.id)}
                    className="text-muted-foreground hover:text-foreground inline-flex size-3.5 items-center justify-center rounded-full transition-colors"
                    aria-label={`Remove ${list.title}`}
                  >
                    <X className="size-3" />
                  </button>
                </Badge>
              ))}
            </div>
          ) : null}

          <CampaignCreatorInputCanvas rows={creatorInputRows} validation={creatorInputValidation} />
        </div>

        <div className="space-y-3">
          <FormSectionHeader
            title="Add keywords"
            titleSuffix={
              <FieldInfoIcon
                ariaLabel="Keyword input guide"
                content="Press Enter or use a comma to add each keyword. You can track keywords, hashtags, and mentions in the post body, like sale, #springdrop, or @brandname."
              />
            }
            description="Posts are tracked if they match any keyword below."
          />

          <div className="space-y-3">
            <CampaignKeywordInputCanvas
              inputValue={keywords.creatorKeywordInput}
              keywords={keywords.creatorKeywords}
              onBlur={keywords.commitCreatorKeywordInput}
              onChange={keywords.handleCreatorKeywordInputChange}
              onKeyDown={keywords.handleCreatorKeywordKeyDown}
              onRemoveKeyword={keywords.handleRemoveCreatorKeyword}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
